// useSubmitGuard (SIM-10 / ACS-4134, SUB-TASK-2): shared submit guard for the new
// /signin flow. Prevents double-submit (BR-024/AC-025), shows a delayed loader after
// 200ms via the REUSED withInterstice decorator (BR-026/AC-026), and surfaces a 10s
// timeout message while re-enabling the button. A late response that arrives after the
// timeout is ignored so it can never establish a second session.
//
// Consumed by SIM-6/7/8 submit actions (Send Code, Verify, Sign In, Reset Password).
import {
    useCallback, useEffect, useRef, useState
} from 'react';
import decorators from 'utils/decorators';
import store from 'Store';
import Actions from 'Actions';

// Delay before the loader appears (AC-026: within 200ms).
const LOADER_DELAY_MS = 200;
// How long to wait before surfacing the timeout message + re-enabling (BR-026).
const SUBMIT_TIMEOUT_MS = 10000;

/**
 * Guard a submit action against double-submit, with a 200ms delayed loader and a
 * 10s timeout.
 * @param {Function} action - The async submit action to run (returns a Promise).
 * @returns {{ submit: Function, inFlight: boolean, showTimeout: boolean }}
 */
const useSubmitGuard = action => {
    const [inFlight, setInFlight] = useState(false);
    const [showTimeout, setShowTimeout] = useState(false);

    // Refs so the in-flight/settled checks are synchronous and immune to stale
    // closures across the async action lifecycle.
    const inFlightRef = useRef(false);
    const settledRef = useRef(false);
    const timeoutIdRef = useRef(null);

    const clearTimer = useCallback(() => {
        if (timeoutIdRef.current !== null) {
            clearTimeout(timeoutIdRef.current);
            timeoutIdRef.current = null;
        }
    }, []);

    // Clean up any pending timer if the component unmounts mid-submit.
    useEffect(() => clearTimer, [clearTimer]);

    const submit = useCallback(
        async (...args) => {
            // BR-024/AC-025: ignore a second submit while one is in flight.
            if (inFlightRef.current) {
                return undefined;
            }

            inFlightRef.current = true;
            settledRef.current = false;
            setInFlight(true);
            setShowTimeout(false);

            // BR-026/AC-026: after 10s, surface the timeout message and re-enable the
            // button. Mark settled so a late success is ignored (no second session).
            timeoutIdRef.current = setTimeout(() => {
                settledRef.current = true;
                inFlightRef.current = false;
                timeoutIdRef.current = null;
                // withInterstice only hides the global interstice when its wrapped promise
                // settles (utils/decorators.js). On a hung/slow request the overlay would stay
                // up past the timeout and hide the timeout message, blocking retry — so force it
                // down here. The late response is still ignored via settledRef above.
                store.dispatch(Actions.showInterstice(false));
                setShowTimeout(true);
                setInFlight(false);
            }, SUBMIT_TIMEOUT_MS);

            // Reuse withInterstice for the 200ms delayed loader (do NOT hand-roll it).
            const runWithLoader = decorators.withInterstice(action, LOADER_DELAY_MS);

            try {
                const data = await runWithLoader(...args);

                // A response that arrives after the timeout fired is ignored.
                if (settledRef.current) {
                    return undefined;
                }

                clearTimer();
                settledRef.current = true;
                inFlightRef.current = false;
                setInFlight(false);

                return data;
            } catch (error) {
                // Tear down and re-enable, then rethrow for the orchestrator to route.
                if (!settledRef.current) {
                    clearTimer();
                    settledRef.current = true;
                    inFlightRef.current = false;
                    setInFlight(false);
                }

                throw error;
            }
        },
        [action, clearTimer]
    );

    return { submit, inFlight, showTimeout };
};

export default useSubmitGuard;
